import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { BrandFlourish } from "@/components/BrandFlourish";

const WordSearch = ({ pz, showSolution }) => {
  const grid = pz.grid;
  const n = pz.size || grid.length;
  const S = 450, cell = S / n;
  const line = (p) => {
    const L = p.word.length;
    const x0 = p.col * cell + cell / 2;
    const y0 = p.row * cell + cell / 2;
    const x1 = (p.col + p.dc * (L - 1)) * cell + cell / 2;
    const y1 = (p.row + p.dr * (L - 1)) * cell + cell / 2;
    return { x0, y0, x1, y1 };
  };
  return (
    <div>
      <svg viewBox={`0 0 ${S} ${S}`} width="100%" style={{ maxWidth: 460 }} className="mx-auto block">
        <rect x="0" y="0" width={S} height={S} fill="#fff" />
        {showSolution && (pz.placements || []).map((p, i) => {
          const l = line(p);
          return <line key={i} x1={l.x0} y1={l.y0} x2={l.x1} y2={l.y1} stroke="#dc2626" strokeWidth={cell * 0.7} strokeLinecap="round" opacity="0.35" />;
        })}
        {grid.map((row, r) => row.map((ch, col) => (
          <text key={`${r}-${col}`} x={col * cell + cell / 2} y={r * cell + cell * 0.68} textAnchor="middle"
            fontSize={cell * 0.6} fontFamily="Helvetica, Arial" fill="#111827">{ch}</text>
        )))}
      </svg>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1">
        {pz.words.map((w) => <span key={w} className="font-ui text-sm text-stone-700 uppercase tracking-wide">{w}</span>)}
      </div>
    </div>
  );
};

const Sudoku = ({ grid, givens }) => {
  const S = 450, c = S / 9;
  return (
    <svg viewBox={`0 0 ${S} ${S}`} width="100%" style={{ maxWidth: 460 }} className="mx-auto block">
      <rect x="0" y="0" width={S} height={S} fill="#fff" />
      {grid.map((row, r) => row.map((v, col) => v ? (
        <text key={`${r}-${col}`} x={col * c + c / 2} y={r * c + c * 0.7} textAnchor="middle"
          fontSize={c * 0.55} fontFamily="Georgia, serif"
          fill={givens[r][col] ? "#111827" : "#2563eb"} fontWeight={givens[r][col] ? 700 : 500}>{v}</text>
      ) : null))}
      {[...Array(10)].map((_, i) => (
        <g key={i}>
          <line x1={i * c} y1={0} x2={i * c} y2={S} stroke="#111827" strokeWidth={i % 3 === 0 ? 2.4 : 0.6} />
          <line x1={0} y1={i * c} x2={S} y2={i * c} stroke="#111827" strokeWidth={i % 3 === 0 ? 2.4 : 0.6} />
        </g>
      ))}
    </svg>
  );
};

const Maze = ({ maze, showSolution }) => {
  const { w, h, cells, mask } = maze;
  const cell = 460 / Math.max(w, h);
  const N = 1, E = 2, S = 4, W = 8;
  const [sx, sy] = maze.start || [0, 0];
  const [ex, ey] = maze.end || [w - 1, h - 1];
  const lines = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (mask && mask[y][x] !== "1") continue;
      let m = cells[y][x];
      if (x === sx && y === sy) m |= maze.start_dir || 0;
      if (x === ex && y === ey) m |= maze.end_dir || 0;
      const px = x * cell, py = y * cell;
      if (!(m & N)) lines.push([px, py, px + cell, py]);
      if (!(m & S)) lines.push([px, py + cell, px + cell, py + cell]);
      if (!(m & W)) lines.push([px, py, px, py + cell]);
      if (!(m & E)) lines.push([px + cell, py, px + cell, py + cell]);
    }
  }
  const pts = (maze.solution || []).map(([x, y]) => `${x * cell + cell / 2},${y * cell + cell / 2}`).join(" ");
  const fs = Math.max(6, cell * 0.62);
  return (
    <svg data-testid={mask ? "shaped-maze" : "square-maze"} viewBox={`-2 -2 ${w * cell + 4} ${h * cell + 4}`} width="100%" style={{ maxWidth: 480 }} className="mx-auto block">
      {!mask && <rect x="0" y="0" width={w * cell} height={h * cell} fill="#fff" />}
      {showSolution && pts && (
        <polyline points={pts} fill="none" stroke="#dc2626" strokeWidth={Math.max(1.5, cell * 0.16)} strokeLinejoin="round" strokeLinecap="round" />
      )}
      {lines.map((l, i) => <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} stroke="#111827" strokeWidth={mask ? 1.1 : 1.4} strokeLinecap="round" />)}
      <text x={sx * cell + cell / 2} y={sy * cell + cell / 2 + fs * 0.36} textAnchor="middle" fontSize={fs} fill="#16a34a" fontWeight={700}>S</text>
      <text x={ex * cell + cell / 2} y={ey * cell + cell / 2 + fs * 0.36} textAnchor="middle" fontSize={fs} fill="#dc2626" fontWeight={700}>E</text>
    </svg>
  );
};

const Doodle = ({ id, className }) => (
  <img data-testid="maze-doodle" src={imageUrl(id)} alt="" aria-hidden="true"
    className={`absolute w-14 h-14 sm:w-16 sm:h-16 object-contain pointer-events-none ${className}`} />
);

const WritingLines = ({ style = "ruled", rows = 8 }) => {
  if (style === "blank") return <div style={{ minHeight: rows * 26 }} />;
  if (style === "dot") {
    return (
      <div className="grid" style={{ gridTemplateColumns: "repeat(14, 1fr)", gap: 20, padding: "6px 2px" }}>
        {Array.from({ length: rows * 14 }).map((_, i) => (
          <span key={i} className="inline-block rounded-full bg-stone-300" style={{ width: 3, height: 3 }} />
        ))}
      </div>
    );
  }
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="border-b border-gold-500/20" style={{ height: 26 }} />
      ))}
    </div>
  );
};

const Callout = ({ label, text }) => (
  <div data-testid="wb-callout" className="my-5 rounded-xl border-l-4 border-gold-500 bg-gold-50/70 px-4 py-3">
    <div className="font-ui text-[11px] font-bold uppercase tracking-[0.18em] text-gold-700 mb-1">{label || "Try This"}</div>
    <p className="font-read text-stone-700 leading-relaxed">{text}</p>
  </div>
);

const WritingPage = ({ chapter }) => {
  const p = chapter.puzzle || {};
  const workbook = p.type === "workbook";
  const [answer, setAnswer] = useState(false);
  const hasAnswers = workbook && (p.answers || []).length > 0;
  const co = p.callout && p.callout.text ? p.callout : null;
  const textLen = `${p.intro || ""}${p.lesson || ""}${p.example || ""}${(p.questions || []).join("")}`.trim().length;
  const sparse = workbook && !chapter.image_id && textLen < 140;
  return (
    <div data-testid="writing-view" className="text-left">
      <div className="font-ui text-xs uppercase tracking-[0.25em] text-slate-400 mb-1 text-center">{chapter.title}</div>
      {workbook && p.subtitle && (
        <div className="font-read italic text-slate-400 text-center mb-4">{p.subtitle}</div>
      )}
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl w-full max-w-2xl mx-auto text-stone-800">
        {workbook && chapter.image_id && (
          <figure className="mb-6 -mt-1">
            <img
              data-testid="wb-illustration"
              src={`${imageUrl(chapter.image_id)}?t=${chapter.image_id}`}
              alt={chapter.title}
              className="w-full rounded-xl object-cover h-44 sm:h-56"
            />
          </figure>
        )}
        {p.prompt && <p className="font-read italic text-lg text-stone-700 mb-5">{p.prompt}</p>}
        {workbook ? (
          <div>
            {p.microcopy && (
              <p className="font-ui text-xs uppercase tracking-[0.2em] text-gold-700 mb-4">{p.microcopy}</p>
            )}
            {p.intro && <p className="font-read italic text-stone-600 mb-4 leading-relaxed">{p.intro}</p>}
            {p.lesson && <p className="font-read text-stone-700 mb-5 leading-relaxed">{p.lesson}</p>}
            {p.example && (
              <div className="my-5 rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
                <div className="font-ui text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Example</div>
                <p className="font-read text-stone-700 leading-relaxed">{p.example}</p>
              </div>
            )}
            {p.key_insight && (
              <div className="my-5 rounded-xl border-l-4 border-gold-400 bg-gold-50/60 px-4 py-3">
                <div className="font-ui text-[11px] font-bold uppercase tracking-[0.18em] text-gold-700 mb-1">Key Insight</div>
                <p className="font-read text-stone-700 leading-relaxed">{p.key_insight}</p>
              </div>
            )}
            {co && <Callout label={co.label} text={co.text} />}
            {(p.questions || []).map((q, i) => (
              <div key={i} className="mb-5">
                <div className="font-ui font-semibold text-sm text-stone-900 mb-2">{i + 1}. {q}</div>
                {hasAnswers && answer
                  ? <div data-testid={`wb-answer-${i}`} className="font-read text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{p.answers[i]}</div>
                  : <WritingLines style={p.lines} rows={3} />}
              </div>
            ))}
            {p.reflection && (
              <div className="mt-6 pt-5 border-t border-stone-200">
                <div className="font-ui text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">Reflection</div>
                <p className="font-read italic text-stone-700 mb-3">{p.reflection}</p>
                <WritingLines style={p.lines} rows={2} />
              </div>
            )}
            {sparse && <BrandFlourish />}
          </div>
        ) : (
          <WritingLines style={p.lines} rows={12} />
        )}
      </div>
      {hasAnswers && (
        <div className="mt-6 text-center">
          <button data-testid="workbook-answer-toggle" onClick={() => setAnswer((a) => !a)}
            className="inline-flex items-center gap-2 font-ui text-sm font-medium text-slate-200 border border-gold-500/12 hover:border-gold-300 rounded-lg px-4 py-2 transition-colors">
            {answer ? <><EyeOff className="w-4 h-4" /> Hide answers</> : <><Eye className="w-4 h-4" /> Show answers</>}
          </button>
        </div>
      )}
    </div>
  );
};

export const PuzzleView = ({ chapter }) => {
  const [answer, setAnswer] = useState(false);
  const p = chapter?.puzzle;
  if (!p) return null;
  if (p.type === "writing" || p.type === "workbook") return <WritingPage chapter={chapter} />;
  const heading = { sudoku: `Sudoku · ${p.difficulty}`, maze: "Maze", wordsearch: "Word Search", coloring: "Color this page" }[p.type] || "Puzzle";
  const hasAnswer = p.type === "sudoku" || p.type === "maze" || p.type === "wordsearch";
  return (
    <div data-testid="puzzle-view" className="text-center">
      {(chapter.title || "").toLowerCase().includes(heading.split(" · ")[0].toLowerCase()) ? (
        <h2 data-testid="puzzle-title" className="font-display text-2xl font-bold text-stone-800 mb-6">{chapter.title}</h2>
      ) : (
        <>
          <div className="font-ui text-xs uppercase tracking-[0.25em] text-slate-400 mb-1">{chapter.title}</div>
          <h2 data-testid="puzzle-title" className="font-display text-2xl font-bold text-stone-800 mb-6 capitalize">{heading}</h2>
        </>
      )}
      <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-2xl inline-block w-full max-w-xl">
        {p.type === "sudoku" && <Sudoku grid={answer ? p.solution : p.grid} givens={p.grid} />}
        {p.type === "maze" && (
          <div className={`relative ${p.doodles?.length ? "py-14 sm:py-16" : ""}`}>
            {p.doodles?.[0] && <Doodle id={p.doodles[0]} className="top-0 left-0" />}
            {p.doodles?.[1] && <Doodle id={p.doodles[1]} className="bottom-0 right-0" />}
            <Maze maze={p} showSolution={answer} />
          </div>
        )}
        {p.type === "wordsearch" && <WordSearch pz={p} showSolution={answer} />}
        {p.type === "coloring" && (p.image_id
          ? <img data-testid="coloring-image" src={`${imageUrl(p.image_id)}?t=${p.image_id}`} alt={p.scene || "coloring page"} className="w-full rounded-lg" />
          : <div className="py-16 text-slate-400 font-read">Line art unavailable for this page.</div>)}
      </div>
      {hasAnswer && (
        <div className="mt-6">
          <button
            data-testid="puzzle-answer-toggle"
            onClick={() => setAnswer((a) => !a)}
            className="inline-flex items-center gap-2 font-ui text-sm font-medium text-slate-200 border border-gold-500/12 hover:border-gold-300 rounded-lg px-4 py-2 transition-colors"
          >
            {answer ? <><EyeOff className="w-4 h-4" /> Hide answer</> : <><Eye className="w-4 h-4" /> Show answer</>}
          </button>
        </div>
      )}
    </div>
  );
};
